import {
  ACTION_TYPE,
  AVAILABLE_LANGUAGES,
  BUTTON_ACTION_TYPE,
  ButtonAction,
  ButtonComponent,
  COMPONENT_TYPE,
  CONSULTATION_STEPS,
  ERROR_TYPES,
  FormComponents,
  Module,
  MODULE_TYPE,
  ROUTE_CONDITION_TYPE,
  Row,
  STEP_TYPE,
  User
} from '@abiglobalhealth/go-types'
import axios, { AxiosError } from 'axios'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { setChoice } from '@/api/choices'
import {
  createFollowUpConsultation,
  createMedicalConsultation,
  createMentalHealthEvaluation,
  createPrescriptionRequest,
  createVideoConsultation
} from '@/api/consultations'
import { setIndicationAnswers } from '@/api/indications'
import { getNextModule } from '@/api/modules'
import { setPharmacyChoice } from '@/api/pharmacy'
import { setReminder } from '@/api/reminders'
import { handleSession } from '@/api/session'
import { setAssessmentSurveyAnswers } from '@/api/surveys'
import { resetTraces } from '@/api/traces'
import { createUser, getRouteConditions, setUserInfo, updateUser } from '@/api/user'
import { UserFormData } from '@/contexts/userForm'
import { useChoices } from '@/hooks/useChoices'
import { useConsultation } from '@/hooks/useConsultation'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useGeneralErrors } from '@/hooks/useGeneralErrors'
import { useIndications } from '@/hooks/useIndications'
import { useLoading } from '@/hooks/useLoading'
import { useModal } from '@/hooks/useModal'
import { useModule } from '@/hooks/useModule'
import { useNavigate } from '@/hooks/useNavigate'
import { usePharmacy } from '@/hooks/usePharmacy'
import { usePostMessage } from '@/hooks/usePostMessage'
import { useQuestion } from '@/hooks/useQuestion'
import { useReminder } from '@/hooks/useReminder'
import { useStep } from '@/hooks/useStep'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { useUserForm } from '@/hooks/useUserForm'
import i18n from '@/locale/i18n'
import { ERROR_TYPE, getErrorMessage, ResponseError } from '@/utils/errors'
import { isNavigatorModule } from '@/utils/navigator'
import { getRouteByConditions } from '@/utils/route'

import useLimitExceeded from './useLimitExceeded'
import { useTagManager } from './useTagManager'

export const useButtonClick = () => {
  const { t } = useTranslation()
  const { setReminderSet } = useReminder()
  const { navigateToStep, navigateToModule, returnToStep } = useNavigate()
  const { currentStep, setCurrentStep } = useStep()
  const { currentModule } = useModule()
  const { traces } = useTraces()
  const { open: openModal, close: closeModal } = useModal()
  const { getUserDataById, getUserData, setUserUpdatedFields, userUpdatedFields } = useUserForm()
  const { user, updateUser: updateUserObject } = useUser()
  const { choices } = useChoices()
  const { addGeneralError, clearGeneralErrors } = useGeneralErrors()
  const { formErrors } = useFormErrors()
  const { addFormError, setFormErrors } = useFormErrors()
  const { showLoading, hideLoading } = useLoading()
  const { currentConsultationStep, nextStep: nextConsultationStep } = useConsultationStep()
  const { question, resetQuestion } = useQuestion()
  const { updateConsultation } = useConsultation()
  const { indications } = useIndications()
  const { selectedPharmacy } = usePharmacy()
  const { checkLimitExceeded } = useLimitExceeded()
  const { sendGTMEvent } = useTagManager()
  const { trackNewConsultation } = usePostMessage()

  const shouldValidateForm = useMemo(() => {
    if (currentStep?.type === STEP_TYPE.FULFILLMENT_CHOICE_SELECTION) return false

    if (currentModule?.type === MODULE_TYPE.CONSULTATION_LANDING) return true

    const stepTypes = [STEP_TYPE.CHOICES, STEP_TYPE.USER_FORM, STEP_TYPE.INDICATION_SURVEY]
    if (currentStep?.type && stepTypes.includes(currentStep.type)) return true

    const consultationSteps = [CONSULTATION_STEPS.FORM, CONSULTATION_STEPS.LANDING]
    if (currentConsultationStep && consultationSteps.includes(currentConsultationStep)) return true

    return false
  }, [currentModule?.type, currentStep?.type, currentConsultationStep])

  const validateComponent = (component: FormComponents) => {
    const { id, required } = component
    const currentFieldError = formErrors ? formErrors[id] : null

    if (currentFieldError) {
      return id
    }

    const value = component.componentType === COMPONENT_TYPE.COUNTRY_SELECT ? getUserDataById('country') : getUserDataById(id)
    const choice = choices?.find(({ component, stepId }) => component.id === id && currentStep?.id === stepId)

    if (required && !value && !choice) {
      addFormError(id, 'error.validation.required')

      return id
    }

    return null
  }

  const validateForm = () => {
    if (currentModule?.type === MODULE_TYPE.CONSULTATION_LANDING && currentStep?.type !== STEP_TYPE.USER_FORM) {
      if (!question.question) {
        addFormError('question', 'error.validation.required')
        return false
      }

      return !formErrors?.question
    }

    switch (currentStep?.type) {
      case STEP_TYPE.CHOICES:
      case STEP_TYPE.USER_FORM: {
        const errorComponents = currentStep?.componentsOrder.map((componentId) => {
          const component = currentStep?.components[componentId] as FormComponents | Row

          if (component.componentType === COMPONENT_TYPE.ROW) {
            // If component is a row, we need to validate each component inside the row
            const errorComponents = component.components.map((rowComponent) => validateComponent(rowComponent as FormComponents))

            return errorComponents.some(Boolean)
          }

          return validateComponent(component)
        })

        return !errorComponents.some(Boolean)
      }

      case STEP_TYPE.INDICATION_SURVEY: {
        const requiredComponent = Object.values(currentStep?.components).find((component) => (component as FormComponents).required)

        if (!requiredComponent) return true

        const currentIndication = indications.find(({ id }) => id === currentStep?.id)
        if (!currentIndication || !currentIndication.answers) return true

        const isEmpty = currentIndication.answers.some((answer) => !answer.value)
        if (isEmpty) addFormError(requiredComponent.id, 'error.validation.required')
        return !isEmpty
      }

      default: {
        return true
      }
    }
  }

  const handleUserAction = async (action: ACTION_TYPE.CREATE_USER | ACTION_TYPE.UPDATE_USER | ACTION_TYPE.SET_USER_INFO) => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    try {
      const payload = {
        user: {
          ...user.data, // Use current user data
          ...getUserData() // Override user data with form data
        }
      }

      let userObj: User | null
      switch (action) {
        case ACTION_TYPE.CREATE_USER:
          userObj = await createUser(payload)
          break

        case ACTION_TYPE.UPDATE_USER: {
          if (!userUpdatedFields.length) {
            console.info('No user updated fields found => SKIP UPDATE_USER')
            userObj = user
            break
          }

          const updatedUserData: User['data'] = userUpdatedFields.reduce(
            (acc, field) => {
              acc[field] = getUserDataById(field)
              return acc
            },
            {
              userId: user.data.userId,
              uniqueId: user.data.uniqueId
            } as Record<string, UserFormData>
          )

          userObj = await updateUser({ user: updatedUserData })
          break
        }

        case ACTION_TYPE.SET_USER_INFO: {
          if (!currentStep) throw new Error('No current step found')
          if (currentStep.type === STEP_TYPE.FULFILLMENT_CHOICE_SELECTION) {
            throw new Error('Fulfillment choice selection step is not a user form step')
          }
          // Only send user data filled on the current step
          const currentStepUserData = Object.values(currentStep.components).reduce(
            (acc, component) => {
              if (component.componentType === COMPONENT_TYPE.ROW) {
                // If component is a row, we need to check each component inside the row
                const rowComponents = (component as Row).components as FormComponents[]
                rowComponents.forEach((rowComponent) => {
                  acc[rowComponent.id] = getUserDataById(rowComponent.id)
                })
              } else {
                acc[component.id] = getUserDataById(component.id)
              }

              return acc
            },
            {} as { [key: string]: UserFormData }
          )

          userObj = await setUserInfo({
            uniqueId: user.data.uniqueId,
            userId: user.data.userId,
            ...currentStepUserData
          })
          break
        }

        default:
          userObj = null
          break
      }

      if (!userObj) throw new Error('No user found')

      updateUserObject(userObj)
      return userObj
    } catch (e) {
      console.error(e)
      if (axios.isAxiosError(e)) {
        const error = e as AxiosError<ResponseError>
        const errorData = error.response?.data
        if (errorData?.type === ERROR_TYPE.USER_FIELDS_ERROR) {
          console.error('form errors', e)
          const formErrors = errorData?.message as any // TODO remove this any
          if (!formErrors) throw new Error('No form errors found')

          const parsedFormErrors = formErrors.reduce(
            (
              acc: {
                [key: string]: string
              },
              error: {
                field: string
                message: {
                  type: string
                }[]
              }
            ) => {
              acc[error.field] = error.message[0].type
              return acc
            },
            {}
          )

          setFormErrors(parsedFormErrors)

          if (!('steps' in currentModule!)) return

          const errorTrace = traces.find((trace) =>
            Object.keys(parsedFormErrors).some(
              (errorField) => 'stepId' in trace && currentModule?.steps && currentModule?.steps[trace.stepId].components[errorField]
            )
          )

          if (!errorTrace) throw new Error('No step found for error field')

          if ('stepId' in errorTrace) returnToStep(errorTrace.stepId)

          return
        }
      }

      throw e
    }
  }

  const handleSetReminder = async (
    action: ACTION_TYPE.SET_REMINDER_PHYSICIAN | ACTION_TYPE.SET_REMINDER_PHARMACY | ACTION_TYPE.SET_REMINDER_WEEKLY
  ) => {
    if (!currentStep) throw new Error('No current step found')
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    const choice = choices.find((choice) => choice.stepId === currentStep?.id)
    const option = choice?.options ? choice.options[0] : null

    await setReminder({
      uniqueId: user?.data?.uniqueId,
      reminder: option?.value.key,
      action
    })

    setReminderSet(option?.value.key !== undefined && option?.value.key !== 'not-remind')
  }

  const handleSetChoice = async () => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')
    if (!choices || choices.length === 0) throw new Error('No choices found')

    const lastChoice = choices[choices.length - 1]

    await setChoice({
      uniqueId: user?.data?.uniqueId,
      choice: lastChoice
    })
  }

  const handleSetIndicationAnswers = async () => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')
    if (!indications || indications.length === 0) throw new Error('No indications found')
    if (currentModule?.type !== MODULE_TYPE.INDICATION_SURVEY) throw new Error('Current module is not an indication survey')

    await setIndicationAnswers({
      uniqueId: user?.data?.uniqueId,
      indicationSurvey: {
        id: Number(currentModule?.id.split('-')[1]),
        titleKey: currentModule.name,
        title: t(currentModule.name),
        indications
      }
    })

    sendGTMEvent('prescriptionRequestRequest_submit_questionnaire')
  }

  const handleSetAssessmentSurveyAnswers = async () => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')
    if (!indications || indications.length === 0) throw new Error('No indications found')
    if (currentModule?.type !== MODULE_TYPE.INDICATION_SURVEY) throw new Error('Current module is not an indication survey')

    await setAssessmentSurveyAnswers({
      uniqueId: user?.data?.uniqueId,
      assessmentSurveyAnswer: {
        id: Number(currentModule?.id.split('-')[1]),
        title: t(currentModule.name),
        questions: indications.map((indication) => ({
          id: Number(indication.id),
          question: t(indication.questionKey),
          answers: indication.answers.map((answer) => ({
            id: Number(answer.id),
            value: answer.value
          }))
        }))
      }
    })
  }

  const handleGetRouteConditions = async (type: ROUTE_CONDITION_TYPE) => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    const userWithUpdatedRouteConditions = await getRouteConditions({
      uniqueId: user?.data?.uniqueId,
      type
    })

    return userWithUpdatedRouteConditions
  }

  const handleNavigationPreviousActions = async (
    buttonAction: ButtonAction<
      BUTTON_ACTION_TYPE.NEXT_STEP | BUTTON_ACTION_TYPE.NEXT_MODULE | BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM | BUTTON_ACTION_TYPE.RELOAD_PAGE
    >
  ) => {
    if (!buttonAction.previousActions) throw new Error('No previous actions found')

    let updatedUser: User | null = user

    for (const previousAction of buttonAction.previousActions) {
      // TODO this is for retro-compatibility, remove on next release
      const legacyPreviousAction = typeof previousAction === 'string' ? { action: previousAction } : previousAction

      // Check consultation limit exceeded
      if (
        [
          ACTION_TYPE.CREATE_MEDICAL_CONSULTATION,
          ACTION_TYPE.CREATE_MEDICAL_FOLLOW_UP_CONSULTATION,
          ACTION_TYPE.CREATE_PRESCRIPTION_REQUEST,
          ACTION_TYPE.CREATE_VIDEO_CONSULTATION,
          ACTION_TYPE.CREATE_MENTAL_HEALTH_CONSULTATION
        ].includes(legacyPreviousAction.action)
      ) {
        const limitExceeded = await checkLimitExceeded()
        if (limitExceeded) throw new Error('Consultation limit exceeded')
      }

      switch (legacyPreviousAction.action) {
        case ACTION_TYPE.CREATE_USER:
        case ACTION_TYPE.UPDATE_USER:
        case ACTION_TYPE.SET_USER_INFO: {
          updatedUser = (await handleUserAction(legacyPreviousAction.action)) ?? null
          setUserUpdatedFields([]) // Reset user updated fields
          break
        }

        case ACTION_TYPE.GET_ROUTE_CONDITIONS: {
          updatedUser = await handleGetRouteConditions(legacyPreviousAction.type)
          break
        }

        case ACTION_TYPE.SET_REMINDER_PHYSICIAN:
        case ACTION_TYPE.SET_REMINDER_PHARMACY:
        case ACTION_TYPE.SET_REMINDER_WEEKLY: {
          await handleSetReminder(legacyPreviousAction.action)
          break
        }

        case ACTION_TYPE.SET_CHOICE: {
          await handleSetChoice()
          break
        }

        case ACTION_TYPE.CREATE_MEDICAL_CONSULTATION: {
          const createdConsultation = await createMedicalConsultation({
            question: question.question,
            attachments: question.attachments,
            uniqueId: updatedUser?.data?.uniqueId as string
          })

          sendGTMEvent('medicalConsultation_submit_question')
          trackNewConsultation(createdConsultation.consultationId)
          updateConsultation(createdConsultation)
          resetQuestion() // Reset question
          setUserUpdatedFields([]) // Reset user updated fields

          break
        }

        case ACTION_TYPE.CREATE_MEDICAL_FOLLOW_UP_CONSULTATION: {
          const createdFollowUpConsultation = await createFollowUpConsultation({
            question: question.question,
            attachments: question.attachments,
            uniqueId: updatedUser?.data?.uniqueId as string
          })

          sendGTMEvent('medicalConsultation_submit_followup_question')
          updateConsultation(createdFollowUpConsultation)
          resetQuestion() // Reset question
          setUserUpdatedFields([]) // Reset user updated fields

          break
        }

        case ACTION_TYPE.CREATE_PRESCRIPTION_REQUEST: {
          const createdConsultation = await createPrescriptionRequest({
            question: question.question,
            uniqueId: updatedUser?.data?.uniqueId as string
          })

          sendGTMEvent('prescriptionRequest_submit_question')
          trackNewConsultation(createdConsultation.consultationId)
          updateConsultation(createdConsultation)
          resetQuestion() // Reset question
          setUserUpdatedFields([]) // Reset user updated fields

          break
        }

        case ACTION_TYPE.CREATE_MENTAL_HEALTH_CONSULTATION: {
          const createdConsultation = await createMentalHealthEvaluation({
            question: question.question,
            uniqueId: updatedUser?.data?.uniqueId as string
          })

          sendGTMEvent('mentalHealthEvaluation_submit_question')
          trackNewConsultation(createdConsultation.consultationId)
          updateConsultation(createdConsultation)
          resetQuestion() // Reset question
          setUserUpdatedFields([]) // Reset user updated fields

          break
        }

        case ACTION_TYPE.RESET_TRACES: {
          // Remove traces from local storage
          localStorage.removeItem('traces')

          // Reset traces on BE
          updatedUser = await resetTraces({
            uniqueId: updatedUser?.data?.uniqueId as string
          })
          updateUserObject(updatedUser)

          window.location.reload()
          break
        }

        case ACTION_TYPE.SET_INDICATION_ANSWERS: {
          await handleSetIndicationAnswers()
          break
        }

        case ACTION_TYPE.SET_ASSESSMENT_SURVEY_ANSWERS: {
          await handleSetAssessmentSurveyAnswers()
          break
        }

        case ACTION_TYPE.SET_PHARMACY_CHOICE: {
          if (!selectedPharmacy) throw new Error('No selected pharmacy found')

          await setPharmacyChoice({
            uniqueId: updatedUser?.data?.uniqueId!,
            pharmacyId: String(selectedPharmacy.id)
          })

          sendGTMEvent('prescriptionRequest_select_pharmacy')

          break
        }

        case ACTION_TYPE.HANDLE_SESSION: {
          if (!updatedUser?.data?.uniqueId) throw new Error('No user uniqueId found')

          await handleSession({
            uniqueId: updatedUser?.data?.uniqueId
          })

          break
        }

        case ACTION_TYPE.CREATE_VIDEO_CONSULTATION: {
          const createdConsultation = await createVideoConsultation({
            question: question.question,
            attachments: question.attachments,
            uniqueId: updatedUser?.data?.uniqueId as string
          })

          sendGTMEvent('videoConsultation_submit_question')
          trackNewConsultation(createdConsultation.consultationId)
          updateConsultation(createdConsultation)
          resetQuestion() // Reset question
          setUserUpdatedFields([]) // Reset user updated fields

          break
        }
      }
    }

    return updatedUser
  }

  let updatedModule: Module
  const handleNavigation = async (
    buttonAction: ButtonAction<
      BUTTON_ACTION_TYPE.NEXT_STEP | BUTTON_ACTION_TYPE.NEXT_MODULE | BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM | BUTTON_ACTION_TYPE.RELOAD_PAGE
    >
  ) => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    const updatedUser = buttonAction.previousActions ? await handleNavigationPreviousActions(buttonAction) : user

    switch (buttonAction.type) {
      case BUTTON_ACTION_TYPE.NEXT_MODULE: {
        const requestModule = getRouteByConditions({
          userConditions: updatedUser?.routeConditions,
          routes: buttonAction.routes
        })?.route?.module

        if (!requestModule) throw new Error('No module matching conditions found')
        if (!updatedUser?.data?.uniqueId) throw new Error('No user uniqueId found')

        const { module: nextModule } = await getNextModule({
          uniqueId: updatedUser?.data?.uniqueId,
          moduleId: requestModule
        })

        updatedModule = nextModule

        const skippedStepButtonAction = navigateToModule(nextModule)
        if (skippedStepButtonAction) await handleButtonClick(skippedStepButtonAction)

        break
      }

      case BUTTON_ACTION_TYPE.NEXT_STEP: {
        const { route } = getRouteByConditions({
          userConditions: updatedUser?.routeConditions,
          routes: buttonAction.routes
        })

        if (!route?.step) throw new Error('No route or step found')

        const skippedStepButtonAction = navigateToStep(route.step, updatedModule)
        if (skippedStepButtonAction) await handleButtonClick(skippedStepButtonAction)

        break
      }

      case BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM: {
        const isNav = isNavigatorModule(currentModule)
        if (isNav) {
          setCurrentStep(undefined)
        } else {
          nextConsultationStep()
        }
        break
      }
    }
  }

  const handleButtonClick = async (buttonAction: ButtonComponent['buttonAction']) => {
    clearGeneralErrors()
    showLoading()
    setReminderSet(false)

    try {
      switch (buttonAction?.type) {
        case BUTTON_ACTION_TYPE.OPEN_MODAL: {
          return openModal(buttonAction.modalId)
        }

        case BUTTON_ACTION_TYPE.CLOSE_MODAL: {
          return closeModal()
        }

        case BUTTON_ACTION_TYPE.NEXT_STEP:
        case BUTTON_ACTION_TYPE.NEXT_MODULE:
        case BUTTON_ACTION_TYPE.RELOAD_PAGE:
        case BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM: {
          if (shouldValidateForm) {
            const validForm = validateForm()
            if (!validForm) break
          }

          await handleNavigation(buttonAction)
          break
        }

        case BUTTON_ACTION_TYPE.OPEN_LINK: {
          const selectedLanguage = i18n.language as AVAILABLE_LANGUAGES
          const url = buttonAction.languages?.[selectedLanguage] ? buttonAction.languages[selectedLanguage] : buttonAction.href

          return window.open(url, '_blank')
        }
      }
    } catch (error: any) {
      console.error('handleButtonClick:error', { error, cause: error?.cause })

      const message = getErrorMessage(error)
      return addGeneralError({
        type: ERROR_TYPES.GENERAL,
        message: t(message || 'error.something_went_wrong')
      })
    } finally {
      // Only hide loading if button action is not reload page
      if (buttonAction?.type !== BUTTON_ACTION_TYPE.RELOAD_PAGE) hideLoading()
    }
  }

  return {
    handleButtonClick
  }
}
